import { createTheme } from '@mui/material'
import { border } from './border'
import { button } from './button'
import { themeColor } from './color'
import { shadow } from './shadow'
import { RaGlobalRoot } from './global'
import { typography } from './typography'
import { surface } from './semanticTokens'

const material = createTheme()

export const raOverrides = {
  RaLayout: {
    styleOverrides: {
      root: {
        fontFamily: typography.body,
        backgroundColor: themeColor.slate05,
        ['& .RaLayout-content']: {
          backgroundColor: themeColor.slate05,
          [material.breakpoints.down('sm')]: {
            padding: 0,
          },
          [material.breakpoints.up('sm')]: {
            margin: material.spacing(6),
          },
          [material.breakpoints.up('xs')]: {
            marginTop: material.spacing(9),
          },
          [material.breakpoints.down('xs')]: {
            marginTop: material.spacing(7),
          },
        },
        ['& .RaLayout-appFrame']: {
          marginTop: 0,
        },
      },
    },
  },
  RaList: {
    styleOverrides: {
      root: {
        [material.breakpoints.up('md')]: {
          maxWidth: '100%',
          marginLeft: 0,
          marginRight: 'auto',
          width: '100%',
        },
        ['& .RaList-content']: {
          backgroundColor: 'transparent',
          marginBottom: 0,
          border: border.thin + ' solid ' + border.light,
          borderRadius: 12,
          boxShadow: shadow.card,
          overflow: 'hidden',
        },
        ['& .RaList-bulkActionsDisplayed']: {
          marginTop: 0,
        },
      },
    },
  },
  RaShow: {
    styleOverrides: {
      root: {
        ...RaGlobalRoot,
        ['& .RaShow-noActions']: {
          marginTop: 0,
        },
        ['& .RaShow-main']: {
          ['& .show-tab']: {
            fontSize: 14,
            textTransform: 'initial',
            minWidth: 'auto',
          },
        },
        ['& .RaShow-card']: {
          [material.breakpoints.down('sm')]: {
            backgroundColor: surface.white,
          },
          [material.breakpoints.up('xs')]: {
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          },
        },
      },
    },
  },
  RaTabbedShowLayout: {
    styleOverrides: {
      root: {
        ['& .RaTabbedShowLayout-content']: {
          [material.breakpoints.down('md')]: {
            borderTopLeftRadius: border.radius.md,
            borderTopRightRadius: border.radius.md,
            backgroundColor: surface.white,
          },
          [material.breakpoints.down('lg')]: {
            padding: material.spacing(3),
          },
          [material.breakpoints.up('md')]: {
            padding: material.spacing(3, 6),
          },
        },
      },
    },
  },
  RaSidebar: {
    styleOverrides: {
      root: {
        backgroundColor: themeColor.sidebarBg,
        borderRight: border.thin + ' solid ' + themeColor.sidebarBorder,
        height: 'auto',
        ['& .RaSidebar-fixed']: {
          height: '100%',
          position: 'relative',
          paddingTop: 12,
        },
      },
    },
  },
  RaMenuItemLink: {
    styleOverrides: {
      root: {
        borderRadius: border.radius.sm,
        padding: (props: { sidebarIsOpen?: boolean }): string | number =>
          props.sidebarIsOpen ? material.spacing(2) : 12,
        marginBottom: 2,
        height: 40,
        color: themeColor.sidebarText,
        fontSize: 14,
        fontWeight: 500,
        lineHeight: '20px',
        ['&:hover']: {
          backgroundColor: themeColor.sidebarHover,
          color: themeColor.sidebarTextHover,
        },
        ['&.RaMenuItemLink-active']: {
          backgroundColor: themeColor.sidebarActive,
          color: themeColor.sidebarActiveText,
          fontWeight: 600,
        },
      },
    },
  },
  RaTopToolbar: {
    styleOverrides: {
      root: {
        boxSizing: 'border-box',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        paddingTop: 0,
        width: '100%',
        padding: 0,
        backgroundColor: surface.white,
        border: border.thin + ' solid ' + border.light,
        borderBottom: 0,
        borderRadius: border.card.radius + ' ' + border.card.radius + ' 0 0',
        boxShadow: shadow.card,
      },
    },
  },
  RaBulkActionsToolbar: {
    styleOverrides: {
      root: {
        ['& .RaBulkActionsToolbar-topToolbar']: {
          padding: 0,
          paddingTop: 0,
          display: 'flex',
          alignItems: 'center',
        },
        ['& .RaBulkActionsToolbar-toolbar']: {
          padding: material.spacing(0, 1.5, 0, 1.5),
          backgroundColor: themeColor.slate05,
          borderRadius: border.radius.md,
        },
        ['& .RaBulkActionsToolbar-icon']: {
          marginLeft: 0,
        },
      },
    },
  },
  RaCreate: {
    styleOverrides: {
      root: {
        ...RaGlobalRoot,
        ['& .RaCreate-card']: {
          [material.breakpoints.down('sm')]: {
            backgroundColor: surface.white,
          },
          ['& .MuiGrid-container']: {
            marginTop: material.spacing(-2),
          },
          ['& .MuiCardContent-root']: {
            paddingTop: material.spacing(3),
            paddingLeft: material.spacing(3),
            paddingRight: material.spacing(3),
            ['&:first-of-type']: {
              paddingTop: material.spacing(3),
              paddingLeft: material.spacing(3),
              paddingRight: material.spacing(3),
              [material.breakpoints.down('sm')]: {
                paddingTop: material.spacing(3.5),
                paddingLeft: '.2em',
                paddingRight: '.2em',
              },
            },
          },
        },
      },
    },
  },
  RaEdit: {
    styleOverrides: {
      root: {
        ...RaGlobalRoot,
        ['& .RaEdit-card']: {
          [material.breakpoints.down('sm')]: {
            backgroundColor: surface.white,
          },
          ['& .MuiGrid-container']: {
            marginTop: material.spacing(-2),
          },
          ['& .MuiCardContent-root']: {
            paddingTop: material.spacing(3),
            paddingLeft: material.spacing(3),
            paddingRight: material.spacing(3),
            ['&:first-of-type']: {
              paddingTop: material.spacing(3),
              paddingLeft: material.spacing(3),
              paddingRight: material.spacing(3),
              [material.breakpoints.down('sm')]: {
                paddingTop: material.spacing(3.5),
                paddingLeft: '.2em',
                paddingRight: '.2em',
              },
            },
          },
        },
      },
    },
  },
  RaToolbar: {
    styleOverrides: {
      root: {
        marginBottom: material.spacing(2),
      },
    },
  },
  RaFormInput: {
    styleOverrides: {
      root: {
        ['& .RaFormInput-input']: {
          width: '100%',
        },
      },
    },
  },
  RaFileInput: {
    styleOverrides: {
      root: {
        ['& .RaFileInput-dropZone']: {
          backgroundColor: themeColor.slate05,
          border: border.normal + ' dashed ' + themeColor.slate20,
          borderRadius: border.radius.md,
          color: themeColor.slate60,
          padding: material.spacing(4),
        },
        ['& .RaFileInput-preview']: {
          padding: '12px',
          paddingLeft: 0,
          width: '48px',
          maxHeight: '48px',
        },
        ['& .RaFileInput-removeButton']: {
          display: 'flex',
          background: surface.white,
          borderRadius: border.radius.md,
          boxSizing: 'border-box',
          margin: 0,
        },
      },
    },
  },
  RaFileInputPreview: {
    styleOverrides: {
      root: {
        padding: material.spacing(1.5),
        ['& .RaFileInputPreview-removeIcon']: {
          color: themeColor.slate60
        },
      },
    },
  },
  RaLabeled: {
    styleOverrides: {
      root: {
        ['& .RaLabeled-label']: {
          textTransform: 'uppercase',
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: '0.05em',
          color: themeColor.slate40,
        },
        ['& .RaLabeled-value']: {
          ['& .previews']: {
            display: 'flex'
          },
        },
      },
    },
  },
  RaFilterListItem: {
    styleOverrides: {
      root: {
        marginBottom: material.spacing(.5),
        ['& .RaFilterListItem-listItemButton']: {
          paddingLeft: material.spacing(2),
        },
      },
    },
  },
  RaButton: {
    styleOverrides: {
      root: {
        flexDirection: 'row-reverse',
        textTransform: 'initial',
        ['&.MuiButton-contained']: {
          backgroundColor: button.primary.default.backgroundColor,
          color: button.primary.default.color,
          ['&:hover']: {
            backgroundColor: button.primary.hover.backgroundColor,
            color: button.primary.hover.color,
          },
        },
        ['&.Mui-disabled']: {
          backgroundColor: button.primary.disabled.backgroundColor,
          color: button.primary.disabled.color,
        },
      },
    },
  },
  RaSaveButton: {
    styleOverrides: {
      root: {
        marginLeft: material.spacing(2),
        ['&.MuiButton-contained']: {
          backgroundColor: button.primary.default.backgroundColor,
          color: button.primary.default.color,
          ['&:hover']: {
            backgroundColor: button.primary.hover.backgroundColor,
            color: button.primary.hover.color,
          },
        },
        ['&.Mui-disabled']: {
          backgroundColor: button.primary.disabled.backgroundColor,
          color: button.primary.disabled.color,
        },
      },
    },
  },
  RaBulkDeleteWithConfirmButton: {
    styleOverrides: {
      root: {
        ['& .RaBulkDeleteWithConfirmButton-deleteButton']: {
          backgroundColor: button.delete.default.background,
          padding: button.delete.default.padding,
          color: button.delete.default.color,
          ['& .MuiSvgIcon-root']: {
            fill: button.delete.default.fill,
          },
          ['&:hover']: {
            backgroundColor: button.delete.hover.background,
          },
        },
      },
    },
  },
  RaCreateButton: {
    styleOverrides: {
      root: {
        ['& .RaCreateButton-floating']: {
          bottom: 20,
        },
      },
    },
  },
  RaDatagrid: {
    styleOverrides: {
      root: {
        backgroundColor: surface.white,
        borderRadius: 12,
        overflow: 'hidden',
        ['& .RaDatagrid-headerCell']: {
          borderTop: 0,
          boxSizing: 'border-box',
          color: themeColor.slate50,
          fontSize: 12.5,
          fontWeight: 600,
          textTransform: 'none' as const,
          whiteSpace: 'nowrap',
          padding: '10px 16px',
          backgroundColor: surface.white,
          borderBottom: border.thin + ' solid ' + border.light,
          ['&:first-of-type']: {
            borderTopLeftRadius: 0,
          },
          ['&:last-of-type']: {
            borderTopRightRadius: 0,
          },
        },
        ['& .RaDatagrid-row']: {
          transition: 'background-color 0.1s ease',
          ['&:hover']: {
            backgroundColor: themeColor.slate05 + ' !important',
          },
        },
        ['& .RaDatagrid-rowCell']: {
          borderBottom: border.thin + ' solid ' + border.light,
          color: themeColor.slate80,
          padding: '12px 16px',
          verticalAlign: 'middle',
          fontSize: 14,
          ['&:last-child']: {
            padding: '12px 16px',
          },
        },
        // Column helpers: <Datagrid> cellClassName="col-main" etc.
        ['& .col-main']: { minWidth: 260 },
        ['& .col-nowrap']: { whiteSpace: 'nowrap' },
        ['& .col-date']: { whiteSpace: 'nowrap', color: themeColor.slate60, fontSize: 13, fontVariantNumeric: 'tabular-nums' },
        ['& .col-num']: { whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
      },
    },
  },
  RaImageField: {
    styleOverrides: {
      root: {
        ['& .RaImageField-image']: {
          margin: 0,
          maxWidth: '100%',
        },
      },
    },
  },
  RaCardContentInner: {
    styleOverrides: {
      root: {
        ['&:last-child']: {
          [material.breakpoints.only('xs')]: {
            padding: material.spacing(0),
            paddingBottom: material.spacing(2),
          },
        },
        [material.breakpoints.down('md')]: {
          borderTopLeftRadius: border.radius.md,
          borderTopRightRadius: border.radius.md,
          backgroundColor: surface.white,
          ['&:last-child']: {
            paddingBottom: material.spacing(3)
          },
        },
        [material.breakpoints.down('lg')]: {
          padding: material.spacing(3),
        },
        [material.breakpoints.up('md')]: {
          padding: material.spacing(3, 6),
        },
      },
    },
  },
  RaEmpty: {
    styleOverrides: {
      root: {
        ['& .RaEmpty-icon']: {
          backgroundColor: themeColor.slate05,
          borderRadius: border.radius.md,
          boxSizing: 'border-box',
          height: material.spacing(5),
          marginBottom: material.spacing(1),
          padding: material.spacing(.75),
          width: material.spacing(5),
        },
        ['& .RaEmpty-message']: {
          padding: material.spacing(6),
          boxSizing: 'border-box',
          margin: 0,
          ['& .MuiTypography-root']: {
            fontSize: 14,
            lineHeight: '20px',
          },
        },
        ['& .RaEmpty-toolbar']: {
          marginTop: 0,
          paddingLeft: material.spacing(6),
          paddingRight: material.spacing(6),
          paddingBottom: material.spacing(6),
        },
      },
    },
  },
  RaFilterForm: {
    styleOverrides: {
      root: {
        [material.breakpoints.up('md')]: {
          flex: '0 1 60%',
        },
      }
    }
  }
}
